import { useEffect, useRef, useState } from "react";

import ThumbImg from "../../assets/images/conversations/049.webp";
import PlayIcon  from "../../assets/icons/conversations/play-icon.svg";

/* ─────────────────────────────────────────────
   PREFIX: ec-
───────────────────────────────────────────── */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&family=Roboto:wght@400;500&display=swap');

  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

  .ec-page {
    background: #1A1A1A;
    font-family: 'Roboto', sans-serif;
    color: #fff;
  }
  .ec-inner {
    max-width: 1600px;
    margin: 0 auto;
    padding-left: 102px;
    padding-right: 102px;
  }

  /* ══════════════════════════════════════
     SECTION 1 — HERO
  ══════════════════════════════════════ */
  .ec-hero { padding-top: 60px; padding-bottom: 60px; }
  .ec-hero__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
    align-items: start;
  }
  .ec-hero__left { display: flex; flex-direction: column; gap: 44px; }
  .ec-hero__title {
    font-family: 'Montserrat', sans-serif;
    font-size: 32px;
    font-weight: 700;
    color: #ffffff;
    line-height: 140%;
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .ec-hero__title.ec-visible { opacity: 1; transform: translateY(0); }
  .ec-hero__spotify {
    border-radius: 12px;
    overflow: hidden;
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease 0.18s, transform 0.7s ease 0.18s;
  }
  .ec-hero__spotify.ec-visible { opacity: 1; transform: translateY(0); }
  .ec-hero__spotify iframe {
    display: block; width: 100%; height: 152px; border: none; border-radius: 12px;
  }
  .ec-hero__right {
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease 0.1s, transform 0.7s ease 0.1s;
  }
  .ec-hero__right.ec-visible { opacity: 1; transform: translateY(0); }

  /* thumbnail */
  .ec-yt-thumb {
    position: relative; width: 100%; border-radius: 12px;
    overflow: hidden; cursor: pointer; display: block; background: #000;
  }
  .ec-yt-thumb img {
    width: 100%; height: auto; display: block;
    border-radius: 12px; transition: opacity 0.3s ease;
  }
  .ec-yt-thumb:hover img { opacity: 0.85; }
  .ec-yt-thumb__play {
    position: absolute; top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    background: none; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    pointer-events: none;
  }
  .ec-yt-thumb__play img { width: 40px; height: 44px; }
  .ec-yt-iframe-wrap {
    position: relative; width: 100%; padding-bottom: 56.25%;
    height: 0; border-radius: 12px; overflow: hidden;
  }
  .ec-yt-iframe-wrap iframe {
    position: absolute; top: 0; left: 0;
    width: 100%; height: 100%; border: none;
  }

  /* ══════════════════════════════════════
     SECTION 2 — BODY
  ══════════════════════════════════════ */
  .ec-body {
    padding-bottom: 100px; opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.7s ease, transform 0.7s ease;
  }
  .ec-body.ec-visible { opacity: 1; transform: translateY(0); }
  .ec-body__para {
    font-family: 'Roboto', sans-serif; font-size: 16px;
    font-weight: 400; color: #E2E2E2; line-height: 160%; margin-bottom: 14px;
  }
  .ec-body__para:last-child { margin-bottom: 0; }

  /* ── TABLET (max 768px) ── */
  @media (max-width: 768px) {
    .ec-inner { padding-left: 32px; padding-right: 32px; }
    .ec-hero { padding-top: 32px; padding-bottom: 48px; }
    .ec-hero__grid { grid-template-columns: 1fr 1fr; gap: 24px; }
    .ec-hero__title { font-size: 24px; }
    .ec-hero__left { gap: 16px; }
    .ec-body__para { font-size: 14px; margin-bottom: 12px; }
    .ec-body { padding-bottom: 48px; }
    .ec-yt-thumb__play img { width: 30px; height: 32px; }
  }

  /* ── MOBILE (max 480px) ── */
  @media (max-width: 480px) {
    .ec-inner { padding-left: 16px; padding-right: 16px; }
    .ec-hero { padding-top: 51px; padding-bottom: 30px; }
    .ec-hero__grid { grid-template-columns: 1fr; gap: 30px; }
    .ec-hero__left { gap: 30px; }
    .ec-hero__title { font-size: 30px; }
    .ec-body__para { font-size: 16px; margin-bottom: 10px; }
    .ec-body { padding-bottom: 30px; }
    .ec-yt-thumb__play img { width: 30px; height: 32px; }
  }
`;

function useFadeUp(ref) {
  useEffect(() => {
    if (!ref.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("ec-visible");
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
}

const YT_VIDEO_ID = "aRofZ0EO-jw";

function YouTubeEmbed() {
  const [playing, setPlaying] = useState(false);
  if (playing) {
    return (
      <div className="ec-yt-iframe-wrap">
        <iframe
          src={`https://www.youtube.com/embed/${YT_VIDEO_ID}?autoplay=1&rel=0`}
          title="EP 49 EV Charger OEMs"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }
  return (
    <div className="ec-yt-thumb" onClick={() => setPlaying(true)} role="button" aria-label="Play video">
      <img src={ThumbImg} alt="EP 49 EV Charger OEMs thumbnail" />
      <div className="ec-yt-thumb__play">
        <img src={PlayIcon} alt="Play" />
      </div>
    </div>
  );
}

function HeroSection() {
  const titleRef   = useRef(null);
  const spotifyRef = useRef(null);
  const rightRef   = useRef(null);
  useFadeUp(titleRef);
  useFadeUp(spotifyRef);
  useFadeUp(rightRef);

  return (
    <section className="ec-hero">
      <div className="ec-inner">
        <div className="ec-hero__grid">
          <div className="ec-hero__left">
            <h1 ref={titleRef} className="ec-hero__title">
              EP 49 | EV Charger OEMs – 3 different journeys | Tirex Chargers | Siemens | Quench
            </h1>
            <div ref={spotifyRef} className="ec-hero__spotify">
              <iframe
                src="https://open.spotify.com/embed/episode/3JuYO0qWINYJK5Ifu9gwNE?utm_source=generator&theme=0"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                title="EP 49 Spotify Player"
              />
            </div>
          </div>
          <div ref={rightRef} className="ec-hero__right">
            <YouTubeEmbed />
          </div>
        </div>
      </div>
    </section>
  );
}

function BodySection() {
  const ref = useRef(null);
  useFadeUp(ref);
  return (
    <section ref={ref} className="ec-body">
      <div className="ec-inner">
        <p className="ec-body__para">
          In this episode, we talk to some of the biggest EV Charger OEMs in India, discussing the various challenges in the EV charging space and how Indian charger companies can dominate the global competition. We feature Arth PATEL, Founder &amp; CEO of Tirex Chargers, and Amit KEKARE, Head of E-Mobility at Siemens, along with Ravin MIRCHANDANI, Chief Dream Merchant at Quench EV Chargers and the host of the Energizing India Podcast. This is a fun one!
        </p>
      </div>
    </section>
  );
}

export default function Ep49EVChargerOEMs() {
  return (
    <>
      <style>{CSS}</style>
      <div className="ec-page">
        <HeroSection />
        <BodySection />
      </div>
    </>
  );
}